// vg-binding: P7.D-134
// vg-binding: P7.D-154
//
// apps/vendor/src/pages/application/BankDetailsPage.tsx
//
// Phase 1 Wave 12 Task 99 — Vendor application step 4 (banking).
// Phase 7 Wave 18 Task 78 batch C6 (Amendment #1) — i18n rewrite.

import { useNavigate } from 'react-router-dom';
import {
  Button,
  Form,
  FormField,
  FormLabel,
  FormMessage,
  Input,
  VendorApplicationStepperLayout,
} from '@printway/ui';

import { useTranslation } from '../../lib/i18n/index.js';
import { useApplicationStore } from './ApplicationStore.js';

export function BankDetailsPage(): JSX.Element {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const data = useApplicationStore((s) => s.data.bankDetails);
  const setBankDetails = useApplicationStore((s) => s.setBankDetails);

  const accountsMatch =
    data.accountNumber !== '' &&
    data.confirmAccountNumber !== '' &&
    data.accountNumber === data.confirmAccountNumber;

  const canContinue =
    data.accountHolderName.trim().length > 0 &&
    data.bankName.trim().length > 0 &&
    accountsMatch;

  return (
    <VendorApplicationStepperLayout currentStep={4}>
      <h2 className="text-xl font-semibold text-pw-text">{t('application.bankDetails.heading')}</h2>
      <p className="mt-1 text-sm text-pw-text-muted">
        {t('application.bankDetails.description')}
      </p>

      <Form className="mt-6 space-y-4" onSubmit={(e) => e.preventDefault()}>
        <FormField>
          <FormLabel htmlFor="accountHolderName">{t('application.bankDetails.holderName')}</FormLabel>
          <Input
            id="accountHolderName"
            value={data.accountHolderName}
            onChange={(e) => setBankDetails({ accountHolderName: e.target.value })}
            placeholder={t('application.bankDetails.holderPlaceholder')}
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="bankName">{t('application.bankDetails.bankName')}</FormLabel>
          <Input
            id="bankName"
            value={data.bankName}
            onChange={(e) => setBankDetails({ bankName: e.target.value })}
            placeholder={t('application.bankDetails.bankPlaceholder')}
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="accountNumber">{t('application.bankDetails.accountNumber')}</FormLabel>
          <Input
            id="accountNumber"
            type="text"
            value={data.accountNumber}
            onChange={(e) => setBankDetails({ accountNumber: e.target.value })}
            autoComplete="off"
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="confirmAccountNumber">{t('application.bankDetails.confirmAccountNumber')}</FormLabel>
          <Input
            id="confirmAccountNumber"
            type="text"
            value={data.confirmAccountNumber}
            onChange={(e) => setBankDetails({ confirmAccountNumber: e.target.value })}
            autoComplete="off"
          />
          {data.confirmAccountNumber !== '' && !accountsMatch ? (
            <FormMessage>{t('application.bankDetails.mismatch')}</FormMessage>
          ) : null}
        </FormField>

        <FormField>
          <FormLabel htmlFor="routingNumber">{t('application.bankDetails.routing')}</FormLabel>
          <Input
            id="routingNumber"
            value={data.routingNumber}
            onChange={(e) => setBankDetails({ routingNumber: e.target.value })}
            placeholder={t('application.bankDetails.routingPlaceholder')}
          />
        </FormField>

        <div className="flex items-center justify-between pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/application/documents')}
          >
            {t('application.bankDetails.back')}
          </Button>
          <Button
            type="button"
            disabled={!canContinue}
            onClick={() => navigate('/application/review')}
          >
            {t('application.bankDetails.continue')}
          </Button>
        </div>
      </Form>
    </VendorApplicationStepperLayout>
  );
}

export default BankDetailsPage;
